import { Component, computed, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { BadgeColor, GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyDurationPipe } from '@vendasta/galaxy/pipes';
import { BlankValueComponent } from '@vendasta/galaxy/table';
import { FormatDurationOptions } from '@vendasta/galaxy/utility/date-utils';
import { TimerStatus } from '@vendasta/protos/crm/v1/enums';
import type { Timer } from '@vendasta/protos/crm/v1/types';
import { interval, map, share } from 'rxjs';

export const TimerBadgeTranslationKey = {
  timeLeft: 'LIST_OBJECTS_TABLE.TIMER_CELL.TIME_LEFT',
  overdue: 'LIST_OBJECTS_TABLE.TIMER_CELL.OVERDUE',
  paused: 'LIST_OBJECTS_TABLE.TIMER_CELL.PAUSED',
  notStarted: 'LIST_OBJECTS_TABLE.TIMER_CELL.NOT_STARTED',
  expired: 'LIST_OBJECTS_TABLE.TIMER_CELL.EXPIRED',
  stopped: 'LIST_OBJECTS_TABLE.TIMER_CELL.STOPPED',
  terminated: 'LIST_OBJECTS_TABLE.TIMER_CELL.TERMINATED',
} as const;

export interface TimerBadge {
  color: BadgeColor;
  labelKey: string;
  seconds?: number;
}

const ClockTickMs = 1_000;

const clock$ = interval(ClockTickMs).pipe(
  map(() => Date.now()),
  share(),
);

@Component({
  selector: 'crm-timer-badge',
  templateUrl: './timer-badge.component.html',
  styleUrl: './timer-badge.component.scss',
  imports: [TranslateModule, GalaxyBadgeModule, GalaxyDurationPipe, BlankValueComponent],
})
export class TimerBadgeComponent {
  public readonly timer = input<Timer | null>(null);

  protected readonly badge = computed<TimerBadge | null>(() => {
    const timer = this.timer();
    if (!timer) {
      return null;
    }
    switch (timer.status) {
      case TimerStatus.TIMER_STATUS_ACTIVE: {
        const seconds = this.secondsUntil(timer.currentEnd) ?? timer.duration / 1000;
        return seconds >= 0
          ? { color: 'green', labelKey: TimerBadgeTranslationKey.timeLeft, seconds: Math.max(1, seconds) }
          : { color: 'red', labelKey: TimerBadgeTranslationKey.overdue, seconds: Math.max(1, -seconds) };
      }
      case TimerStatus.TIMER_STATUS_EXPIRED: {
        const seconds = this.secondsUntil(timer.currentEnd);
        return seconds === null
          ? { color: 'red', labelKey: TimerBadgeTranslationKey.expired }
          : { color: 'red', labelKey: TimerBadgeTranslationKey.overdue, seconds: Math.max(1, -seconds) };
      }
      case TimerStatus.TIMER_STATUS_PAUSED:
        return { color: 'grey', labelKey: TimerBadgeTranslationKey.paused };
      case TimerStatus.TIMER_STATUS_INACTIVE:
        return { color: 'grey', labelKey: TimerBadgeTranslationKey.notStarted };
      case TimerStatus.TIMER_STATUS_STOPPED:
        return { color: 'grey', labelKey: TimerBadgeTranslationKey.stopped };
      case TimerStatus.TIMER_STATUS_TERMINATED:
        return { color: 'grey', labelKey: TimerBadgeTranslationKey.terminated };
      default:
        return null;
    }
  });

  protected readonly durationOptions: FormatDurationOptions = { maxUnits: 1 };

  private readonly now = toSignal(clock$, { initialValue: Date.now() });

  private secondsUntil(end: Date | undefined): number | null {
    return end ? (end.getTime() - this.now()) / 1000 : null;
  }
}
